import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { digitsOnly } from '@goodfellas/phone-identity/normalize'

import { goodfellasApi } from '../../apis'
import { useAuth } from '../../contexts/AuthContext'
import { useFeedbackStore, useHydration, useStore } from '../../functions/store'
import { isEqualWithNinthDigit } from '../../functions/utils'
import { LanguageSelector } from '../LanguageSelector'
import Dashboard from './Dashboard'
import IntroFeedback from './IntroFeedback'
import IdentificatorBox from './identificatorBox'

interface AvaliacaoComponentProps {
  /** Alvo direto por uuid (deep link `/evaluate/<uuid>`). Quando presente,
   * dispensa a resolução por telefone e vai direto ao fluxo por perfil. */
  targetUuid?: string
}

export const AvaliacaoComponent: React.FC<AvaliacaoComponentProps> = ({
  targetUuid,
}) => {
  const router = useRouter()
  const hasHydrated = useHydration()

  // Set the arena background, instead of the default one
  useEffect(() => {
    document.body.style.backgroundImage = `url(/images/bg/dark-arena.jpg)`
  }, [])

  const { token } = useAuth()
  const user = useStore((state) => state)
  const ratings = useFeedbackStore((state) => state.ratings)
  const indexSelect = useFeedbackStore(
    (state) => state.feedbackActions.indexSelect,
  )

  const isUserSet = !!(user?.phoneNumber && token)

  const [targetPhone, setTargetPhone] = useState<string | null>(null)
  const [targetNotFound, setTargetNotFound] = useState(false)
  const [resolveTarget, setResolveTarget] = useState<{
    suggestedGroupId?: string
    profileUserUuid?: string
  } | null>(null)
  const forcedStepRef = useRef(false)
  const appliedSelectionRef = useRef(false)

  const targetFromQuery = useMemo(() => {
    const raw = router.query?.avaliar
    const value = Array.isArray(raw) ? raw[0] : raw
    if (!value || typeof value !== 'string') return ''
    return digitsOnly(value)
  }, [router.query?.avaliar])

  // Read target from URL once the router is ready
  useEffect(() => {
    if (!router.isReady) return
    if (!targetFromQuery) return
    setTargetNotFound(false)
    setResolveTarget(null)
    forcedStepRef.current = false
    appliedSelectionRef.current = false
    setTargetPhone(targetFromQuery)
  }, [router.isReady, targetFromQuery])

  // Alvo direto por uuid (deep link `/evaluate/<uuid>`): vai direto ao fluxo
  // por perfil, sem depender da resolução por telefone.
  useEffect(() => {
    if (!targetUuid || !isUserSet) return
    setResolveTarget({ profileUserUuid: targetUuid })
  }, [targetUuid, isUserSet])

  // Resolve target by phone to decide flow: shared group (list) vs profile-only (single card) vs not on platform
  useEffect(() => {
    if (!targetPhone || !isUserSet || targetPhone.length < 10) return
    let cancelled = false
    goodfellasApi
      .post('/evaluation/resolve/phone', { phoneDigits: targetPhone })
      .then((res) => {
        if (cancelled) return
        setResolveTarget({
          suggestedGroupId: res.data?.suggestedGroupId,
          profileUserUuid: res.data?.profileUserUuid,
        })
      })
      .catch(() => {
        if (cancelled) return
        setResolveTarget({})
      })
    return () => {
      cancelled = true
    }
  }, [targetPhone, isUserSet])

  // When we have a shared group (suggestedGroupId), force navigation to feedback step so list loads and we can auto-select the card
  useEffect(() => {
    if (!targetPhone || !isUserSet || !resolveTarget?.suggestedGroupId) return
    if (!user.group?._id) return
    if (forcedStepRef.current) return

    forcedStepRef.current = true
    setTargetNotFound(false)
    if (user.step !== 'feedback') {
      user.setUser({ ...user, step: 'feedback' })
    }
  }, [
    targetPhone,
    isUserSet,
    resolveTarget?.suggestedGroupId,
    user.group?._id,
    user.step,
  ])

  // Once feedback ratings are loaded (group flow), auto-select the target fighter
  useEffect(() => {
    if (!targetPhone || !resolveTarget?.suggestedGroupId) return
    if (!isUserSet) return
    if (!user.group?._id) return
    if (user.step !== 'feedback') return
    if (!ratings || ratings.length === 0) return
    if (appliedSelectionRef.current) return

    appliedSelectionRef.current = true

    const idx = ratings.findIndex((r) =>
      isEqualWithNinthDigit(r.fighterPhone, targetPhone),
    )

    if (idx >= 0) {
      indexSelect(idx)
    } else {
      setTargetNotFound(true)
    }

    // Clean the URL to avoid re-triggering on refresh
    router
      .replace('/avaliacao', undefined, { shallow: true })
      .catch(() => undefined)
  }, [
    targetPhone,
    resolveTarget?.suggestedGroupId,
    isUserSet,
    user.group?._id,
    user.step,
    ratings,
    indexSelect,
    router,
  ])

  // Wait for store hydration to prevent server/client mismatch
  if (!hasHydrated) {
    return (
      <div className="container mx-auto mb-0">
        <div className="flex flex-col lg:flex-row lg:gap-x-14 justify-evenly pt-44">
          <IntroFeedback />
          <IdentificatorBox />
        </div>
        <LanguageSelector />
      </div>
    )
  }

  const profileOnlyFlow =
    resolveTarget?.profileUserUuid && !resolveTarget?.suggestedGroupId
  const targetNotOnPlatform =
    resolveTarget &&
    !resolveTarget.profileUserUuid &&
    !resolveTarget.suggestedGroupId

  return (
    <div className="container mx-auto mb-0">
      {targetNotOnPlatform ? (
        <div className="mx-auto mt-6 max-w-3xl rounded-xl border border-amber-400/40 bg-amber-400/15 px-4 py-3 text-white/90">
          Essa pessoa ainda não está na plataforma. Você pode continuar pela
          lista para escolher quem avaliar.
        </div>
      ) : null}
      {targetNotFound && !profileOnlyFlow ? (
        <div className="mx-auto mt-6 max-w-3xl rounded-xl border border-amber-400/40 bg-amber-400/15 px-4 py-3 text-white/90">
          Não encontramos essa pessoa na sua equipe. Você pode continuar pela
          lista para escolher quem avaliar.
        </div>
      ) : null}
      {!isUserSet ? (
        <div className="flex flex-col lg:flex-row lg:gap-x-14 justify-evenly pt-44">
          <IntroFeedback />
          <IdentificatorBox />
        </div>
      ) : profileOnlyFlow ? (
        <Dashboard
          step="feedback-by-profile"
          profileUserUuid={resolveTarget!.profileUserUuid!}
        />
      ) : (
        <Dashboard step={user.step} />
      )}
      <LanguageSelector />
    </div>
  )
}
